import React from "react";
import {
  FaFacebookF,
  FaTiktok,
  FaInstagram,
  FaXTwitter,
} from "react-icons/fa6";
import Logo from "../../assets/Logo.png";
import { scroller } from "react-scroll";
import { useLocation, useNavigate, Link as RouterLink } from "react-router-dom";
import { ArrowUpRight, Mail, Phone, MapPin, Clock } from "lucide-react";

const GET_STARTED_URL = "https://app.thespherex.com/";

const Footer = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const handleNavClick = (sectionId) => {
    if (location.pathname !== "/") {
      navigate(`/?scrollTo=${sectionId}`);
    } else {
      scroller.scrollTo(sectionId, {
        smooth: true,
        duration: 500,
        offset: -80,
      });
    }
  };

  return (
    <footer className="bg-slate-900 text-gray-300 pt-16 pb-12 px-4 sm:px-6 lg:px-8 border-t border-slate-800">
      <div className="max-w-7xl mx-auto">
        {/* Main Grid */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 pb-12 border-b border-slate-800">

          {/* Col 1: Brand Info (5 cols) */}
          <div className="md:col-span-5 space-y-5">
            <div
              onClick={() => handleNavClick("home")}
              className="cursor-pointer inline-block"
            >
              <img src={Logo} alt="SphereX" className="h-9 w-auto" />
            </div>

            <p className="text-gray-400 text-base leading-relaxed max-w-sm">
              Nigeria’s trusted platform to sell crypto and gift cards for instant cash. Fast bank payouts, sweet rates, and friendly 24/7 service.
            </p>

            <div className="pt-2">
              <a
                href={GET_STARTED_URL}
                className="inline-flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white font-bold text-sm px-6 py-3 rounded-full transition-all duration-200 shadow-md shadow-purple-900/30 transform hover:-translate-y-0.5"
              >
                <span>Get Started Now</span>
                <ArrowUpRight className="w-4 h-4" />
              </a>
            </div>

            {/* Social Icons */}
            <div className="pt-4 space-y-2">
              <span className="text-xs font-bold uppercase tracking-wider text-gray-400 block">
                Connect With Us
              </span>
              <div className="flex items-center space-x-3">
                <a
                  href="https://www.instagram.com/sphere__x"
                  target="_blank"
                  rel="noreferrer"
                  className="bg-slate-800 hover:bg-purple-600 text-gray-300 hover:text-white p-2.5 rounded-full transition-colors"
                  aria-label="Instagram"
                >
                  <FaInstagram className="w-4 h-4" />
                </a>
                <a
                  href="https://www.tiktok.com/@sphere__x"
                  target="_blank"
                  rel="noreferrer"
                  className="bg-slate-800 hover:bg-purple-600 text-gray-300 hover:text-white p-2.5 rounded-full transition-colors"
                  aria-label="TikTok"
                >
                  <FaTiktok className="w-4 h-4" />
                </a>
                <a
                  href="https://facebook.com/thespherex"
                  target="_blank"
                  rel="noreferrer"
                  className="bg-slate-800 hover:bg-purple-600 text-gray-300 hover:text-white p-2.5 rounded-full transition-colors"
                  aria-label="Facebook"
                >
                  <FaFacebookF className="w-4 h-4" />
                </a>
                <a
                  href="https://x.com/thespherex"
                  target="_blank"
                  rel="noreferrer"
                  className="bg-slate-800 hover:bg-purple-600 text-gray-300 hover:text-white p-2.5 rounded-full transition-colors"
                  aria-label="X Twitter"
                >
                  <FaXTwitter className="w-4 h-4" />
                </a>
              </div>
            </div>
          </div>

          {/* Col 2: Navigation Links (3 cols) */}
          <div className="md:col-span-3 space-y-4">
            <h4 className="text-white font-bold text-sm uppercase tracking-wider">
              Quick Links
            </h4>
            <ul className="space-y-2.5 text-sm text-gray-400">
              <li>
                <button
                  onClick={() => handleNavClick("services")}
                  className="hover:text-white transition-colors"
                >
                  Services
                </button>
              </li>
              <li>
                <button
                  onClick={() => handleNavClick("how-it-works")}
                  className="hover:text-white transition-colors"
                >
                  How It Works
                </button>
              </li>
              <li>
                <button
                  onClick={() => handleNavClick("why-us")}
                  className="hover:text-white transition-colors"
                >
                  Why SphereX
                </button>
              </li>
              <li>
                <button
                  onClick={() => handleNavClick("testimonials")}
                  className="hover:text-white transition-colors"
                >
                  User Reviews
                </button>
              </li>
              <li>
                <button
                  onClick={() => handleNavClick("faq")}
                  className="hover:text-white transition-colors"
                >
                  Frequently Asked Questions
                </button>
              </li>
              <li>
                <a
                  href={GET_STARTED_URL}
                  className="text-purple-400 hover:text-purple-300 font-semibold"
                >
                  Sign In to Dashboard &rarr;
                </a>
              </li>
            </ul>
          </div>

          {/* Col 3: Contact & Support (4 cols) */}
          <div className="md:col-span-4 space-y-4">
            <h4 className="text-white font-bold text-sm uppercase tracking-wider">
              Get in Touch
            </h4>
            <ul className="space-y-3 text-sm text-gray-400">
              <li className="flex items-start gap-3">
                <Phone className="w-4 h-4 text-purple-400 shrink-0 mt-0.5" />
                <span>+234 702 544 6361 (Support & WhatsApp)</span>
              </li>
              <li className="flex items-start gap-3">
                <Mail className="w-4 h-4 text-purple-400 shrink-0 mt-0.5" />
                <span>support@thespherex.com</span>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-purple-400 shrink-0 mt-0.5" />
                <span>Onike, Yaba, Lagos, Nigeria</span>
              </li>
              <li className="flex items-start gap-3">
                <Clock className="w-4 h-4 text-purple-400 shrink-0 mt-0.5" />
                <span>24/7 Fast Payouts & Active Support</span>
              </li>
            </ul>
          </div>

        </div>

        {/* Bottom Sub-footer */}
        <div className="pt-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-gray-500">
          <div className="flex items-center gap-6">
            <RouterLink to="/privacy-policy" className="hover:text-gray-300 transition-colors">
              Privacy Policy
            </RouterLink>
            <RouterLink to="/privacy-policy" className="hover:text-gray-300 transition-colors">
              Terms of Service
            </RouterLink>
          </div>
          <p>
            &copy; {new Date().getFullYear()} SphereX Technologies. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
